import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Input, InputNumber, Popconfirm, Progress, Segmented, Select, Space, Switch, Tag, Tooltip,
} from 'antd';
import type { ParameterGroup, ReservoirType, SweetSpotParameterConfig } from './types';
import { calculateAHP } from './sweetSpotEvaluationEngine';
import type {
  DataQualityReport, EvaluationMethodConfig, EvaluationMethodDefinition,
  EvaluationRun, WeightStrategy,
} from './sweetSpotEvaluationEngine';
import SweetSpotHelp from './SweetSpotHelp';
import {
  addCustomParameter, removeCustomParameter, updateCustomParameter,
} from './sweetSpotCustomParameters';
import type { CustomParameterInput } from './sweetSpotCustomParameters';
import { switchWeightScheme, WEIGHT_SCHEME_LABELS } from './sweetSpotWeightSchemes';
import {
  DATA_STATE_META, RUN_STATUS_LABELS,
  SWEETSPOT_WORKFLOW_STATUS_LABELS, TONE_TAG_COLORS,
} from './sweetSpotPresentation';
import type {
  CalculationState, DataState, ResultState, SweetSpotWorkflowStep,
  SweetSpotWorkflowStepKey,
} from './sweetSpotPresentation';

type Props = {
  definitions: EvaluationMethodDefinition[];
  config: EvaluationMethodConfig;
  recommendedParameters: SweetSpotParameterConfig;
  quality: DataQualityReport;
  draftRun?: EvaluationRun;
  history: EvaluationRun[];
  activeRunId?: string;
  busy?: boolean;
  storageError?: string;
  calculationState: CalculationState;
  dataState: DataState;
  resultState: ResultState;
  computeLabel: string;
  computeReason?: string;
  activeStep: SweetSpotWorkflowStepKey;
  workflowSteps: SweetSpotWorkflowStep[];
  enabledParameterCount: number;
  configValid: boolean;
  intervalLabel: string;
  parameterCoverage: Record<string, number>;
  sourceCurveByParameterId: Record<string, string | undefined>;
  availableSourceCurves: Array<{ value: string; label: string; unit?: string }>;
  reservoirType: ReservoirType;
  reservoirOptions: Array<{ value: ReservoirType; label: string }>;
  showRecommendations: boolean;
  showGradeBackground: boolean;
  showAnomalies: boolean;
  reviewView: SweetSpotReviewView;
  onStepChange: (step: SweetSpotWorkflowStepKey) => void;
  onReviewViewChange: (view: SweetSpotReviewView) => void;
  onReservoirTypeChange: (value: ReservoirType) => void;
  onToggleRecommendations: (value: boolean) => void;
  onToggleGradeBackground: (value: boolean) => void;
  onToggleAnomalies: (value: boolean) => void;
  onProceedToDesign: () => void;
  onConfigChange: (config: EvaluationMethodConfig) => void;
  onCompute: () => void;
  onSave: () => void;
  onActivate: (run: EvaluationRun) => void;
  onSelectRun: (run: EvaluationRun) => void;
  onRefreshHistory: () => void;
};

export const REVIEW_STATE_LABELS: Record<'pending' | 'reviewed' | 'confirmed', string> = {
  pending: '待确认',
  reviewed: '已审核',
  confirmed: '已确认',
};

export type SweetSpotReviewView = 'curves' | 'engineering' | 'data';

const EMPTY_CUSTOM_PARAMETER: CustomParameterInput = {
  label: '', unit: '', sourceColumnKey: undefined, direction: 'larger_is_better',
};
const OPTIONAL_PARAMETER_IDS = new Set(['rq.clayContent']);

export default function SweetSpotEvaluationControls(props: Props) {
  const [customParameterGroup, setCustomParameterGroup] = useState<ParameterGroup | null>(null);
  const [editingCustomParameterId, setEditingCustomParameterId] = useState<string | null>(null);
  const [customParameterDraft, setCustomParameterDraft] = useState<CustomParameterInput>(EMPTY_CUSTOM_PARAMETER);
  const [customParameterError, setCustomParameterError] = useState<string>();
  const blockers = props.quality.issues.filter((issue) => issue.severity === 'error');
  const enabledParameters = [...props.config.parameters.rq, ...props.config.parameters.cq]
    .filter((parameter) => parameter.enabled);
  const availableParameterIds = new Set(props.quality.availableIndicatorIds);
  const availableParameters = enabledParameters.filter((parameter) => availableParameterIds.has(parameter.id));
  const missingParameters = enabledParameters.filter((parameter) => !availableParameterIds.has(parameter.id));
  const requiredMissingParameters = missingParameters.filter((parameter) => !OPTIONAL_PARAMETER_IDS.has(parameter.id));
  const optionalMissingParameters = missingParameters.filter((parameter) => OPTIONAL_PARAMETER_IDS.has(parameter.id));
  const dataReadiness = props.quality.pointCount === 0
    ? { label: '未接收数据', tone: 'is-blocked' }
    : props.quality.rqAvailable && props.quality.cqAvailable
      ? { label: '可完整评价', tone: 'is-ready' }
      : props.quality.canCalculate
        ? { label: '仅可单组试算', tone: 'is-warning' }
        : { label: '不可评价', tone: 'is-blocked' };
  const configurationBlockers = blockers.filter((issue) => ![
    'RQ_INSUFFICIENT', 'CQ_INSUFFICIENT', 'EMPTY_DATA',
  ].includes(issue.code));
  const closeCustomParameterForm = () => {
    setCustomParameterGroup(null);
    setEditingCustomParameterId(null);
    setCustomParameterDraft(EMPTY_CUSTOM_PARAMETER);
    setCustomParameterError(undefined);
  };
  const startCustomParameter = (group: ParameterGroup) => {
    setCustomParameterGroup(group);
    setEditingCustomParameterId(null);
    setCustomParameterDraft(EMPTY_CUSTOM_PARAMETER);
    setCustomParameterError(undefined);
  };
  const editCustomParameter = (group: ParameterGroup, parameterId: string) => {
    const parameter = props.config.parameters[group].find((item) => item.id === parameterId);
    if (!parameter?.custom) return;
    setCustomParameterGroup(group);
    setEditingCustomParameterId(parameterId);
    setCustomParameterDraft({
      label: parameter.label,
      unit: parameter.unit,
      sourceColumnKey: parameter.sourceColumnKey,
      direction: parameter.direction === 'smaller_is_better' ? 'smaller_is_better' : 'larger_is_better',
    });
    setCustomParameterError(undefined);
  };
  const submitCustomParameter = () => {
    if (!customParameterGroup) return;
    try {
      const next = editingCustomParameterId
        ? updateCustomParameter(props.config, customParameterGroup, editingCustomParameterId, customParameterDraft)
        : addCustomParameter(props.config, customParameterGroup, customParameterDraft);
      props.onConfigChange(next);
      closeCustomParameterForm();
    } catch (reason) {
      setCustomParameterError(reason instanceof Error ? reason.message : '评价参数保存失败。');
    }
  };
  const computeReason = props.computeReason;
  const resultIsStale = ['parameters_changed', 'data_changed', 'method_changed'].includes(props.resultState);
  const saveReason = props.draftRun
    ? (resultIsStale
      ? '当前结果与输入数据、方法或参数不一致，请先更新评价结果。'
      : props.draftRun.status !== 'draft'
      ? '当前结果版本已保存。'
      : props.draftRun.quality.canSaveFormal ? undefined : props.draftRun.quality.issues.find((issue) => issue.severity === 'error')?.message || '当前草稿未通过正式保存检查。')
    : '尚未生成可保存的评价结果。';
  const activateReason = !props.draftRun
    ? '未选择可激活的已保存版本。'
    : ['parameters_changed', 'data_changed', 'method_changed'].includes(props.resultState)
      ? '当前结果与输入数据、方法或参数不一致，请先更新评价结果。'
    : props.draftRun.status === 'draft'
      ? '草稿状态不可设为当前版本。'
      : props.draftRun.quality.canActivate
        ? undefined
        : props.draftRun.quality.issues.find((issue) => issue.severity === 'error')?.message || '该版本未满足激活条件。';

  const renderDataCheck = () => <>
    <div className="sweetspot-step-heading">
      <h3><span>1</span> 数据检查</h3>
      <Tag color={TONE_TAG_COLORS[DATA_STATE_META[props.dataState].tone]}>{DATA_STATE_META[props.dataState].label}</Tag>
    </div>
    <div className="sweetspot-data-progress">
      <span>DataHub 参数完整度</span><strong>{(props.quality.completeness * 100).toFixed(1)}%</strong>
      <Progress percent={Math.round(props.quality.completeness * 100)} size="small" showInfo={false} status={blockers.length ? 'exception' : 'normal'} />
    </div>
    <div className="sweetspot-datahub-summary">
      <div><span>评价区间</span><strong>{props.intervalLabel}</strong></div>
      <div><span>传入深度点</span><strong>{props.quality.pointCount} 个</strong></div>
      <div><span>已接收参数</span><strong>{availableParameters.length} 项 / 已启用 {enabledParameters.length} 项</strong></div>
      <div><span>评价条件</span><strong className={dataReadiness.tone}>{dataReadiness.label}</strong></div>
    </div>
    <div className="sweetspot-datahub-checks">
      <section>
        <div className="sweetspot-datahub-check-head"><strong>已接收参数</strong><Tag color="success">{availableParameters.length} 项</Tag></div>
        <div className="sweetspot-datahub-parameter-list">
          {availableParameters.length ? availableParameters.map((parameter) => <div key={parameter.id} className="is-available">
            <span><strong>{parameter.label}</strong><small>{parameter.group === 'rq' ? '储层品质 RQ' : '工程品质 CQ'}</small></span>
            <span><b>{props.sourceCurveByParameterId[parameter.id] || 'DataHub 标准参数'}</b><small>覆盖率 {Math.round((props.parameterCoverage[parameter.id] ?? 0) * 100)}%</small></span>
          </div>) : <div className="sweetspot-datahub-empty">DataHub 尚未传入可用评价参数</div>}
        </div>
      </section>
      <section>
        <div className="sweetspot-datahub-check-head"><strong>缺失参数</strong><Tag color={missingParameters.length ? 'error' : 'success'}>{missingParameters.length} 项</Tag></div>
        <div className="sweetspot-datahub-parameter-list">
          {missingParameters.length ? missingParameters.map((parameter) => <div key={parameter.id} className="is-missing">
            <span><strong>{parameter.label}</strong><small>{parameter.group === 'rq' ? '储层品质 RQ' : '工程品质 CQ'}</small></span>
            <span><b>未传入</b><small>{parameter.unit || '无量纲'}</small></span>
          </div>) : <div className="sweetspot-datahub-empty is-complete">评价参数均已接收</div>}
        </div>
      </section>
    </div>
    {props.quality.pointCount === 0 && <Alert type="error" showIcon message="DataHub 未传入评价区间数据" />}
    {requiredMissingParameters.length > 0 && <Alert
      className="sweetspot-parameter-alert"
      type={props.quality.canCalculate ? 'warning' : 'error'}
      showIcon
      message={`缺失 ${requiredMissingParameters.length} 项评价参数`}
      description={requiredMissingParameters.map((parameter) => parameter.label).join('、')}
    />}
    {optionalMissingParameters.length > 0 && <Alert
      className="sweetspot-parameter-alert"
      type="info"
      showIcon
      message={`可选参数未提供：${optionalMissingParameters.map((parameter) => parameter.label).join('、')}`}
      description="不会阻止评价；当前深度点将按其余已启用且有效的指标重新归一化权重。需要考虑泥质或水敏影响时，可在参数配置中主动启用并绑定可靠曲线。"
    />}
    {(props.storageError || configurationBlockers.length > 0) && <div className="sweetspot-blocker-stack">
      {props.storageError && <Alert type="error" showIcon message="版本存储或同步失败" description={props.storageError} />}
      {configurationBlockers.map((issue, index) => <Alert key={`${issue.code}-${issue.parameterId ?? index}`} type="error" showIcon message={issue.message} />)}
    </div>}
  </>;

  const renderMethod = () => <>
    <div className="sweetspot-step-heading"><h3><span>2</span> 评价指标</h3><Tag color="blue">{enabledParameters.length} 项已启用</Tag></div>
    <div className="sweetspot-method-groups">
      {(['rq', 'cq'] as ParameterGroup[]).map((group) => <section key={group} className={`sweetspot-method-group is-${group}`}>
        <header><div><span>{group.toUpperCase()}</span><strong>{group === 'rq' ? '储层品质' : '工程品质'}</strong></div><b>{props.config.parameters[group].filter((item) => item.enabled).length}/{props.config.parameters[group].length} 项启用</b></header>
        <div className="sweetspot-method-list">
          {props.config.parameters[group].map((item) => <div key={item.id} className={`${item.custom ? 'is-custom' : ''}${item.enabled ? '' : ' is-disabled'}`}>
            <span className="sweetspot-method-parameter-name"><strong>{item.label}</strong>{OPTIONAL_PARAMETER_IDS.has(item.id) && <Tag color="blue">可选</Tag>}{item.custom && <Tag color="purple">自定义</Tag>}{!item.enabled && <Tag>未启用</Tag>}</span>
            <span className="sweetspot-method-parameter-meta">
              <span>{item.unit || '无量纲'}</span>
              {item.custom && <>
                <Button type="link" size="small" onClick={() => editCustomParameter(group, item.id)}>编辑</Button>
                <Popconfirm
                  title="移除该评价参数？"
                  description="移除后，该参数不再参与数据检查、权重配置和甜点评价。"
                  okText="移除"
                  cancelText="取消"
                  onConfirm={() => props.onConfigChange(removeCustomParameter(props.config, group, item.id))}
                ><Button type="link" danger size="small">移除</Button></Popconfirm>
              </>}
            </span>
          </div>)}
        </div>
        {customParameterGroup === group ? <div className="sweetspot-custom-parameter-form">
          <div className="sweetspot-custom-parameter-form-head">
            <strong>{editingCustomParameterId ? '编辑评价参数' : `新增${group === 'rq' ? '储层品质' : '工程品质'}参数`}</strong>
            <Button type="text" size="small" onClick={closeCustomParameterForm}>取消</Button>
          </div>
          <div className="sweetspot-custom-parameter-fields">
            <label><span>参数名称</span><Input maxLength={30} placeholder="例如：含水饱和度" value={customParameterDraft.label} onChange={(event) => setCustomParameterDraft((current) => ({ ...current, label: event.target.value }))} /></label>
            <label><span>单位</span><Input maxLength={16} placeholder="例如：%（留空为无量纲）" value={customParameterDraft.unit} onChange={(event) => setCustomParameterDraft((current) => ({ ...current, unit: event.target.value }))} /></label>
            <label><span>评价方向</span><Select value={customParameterDraft.direction} options={[
              { value: 'larger_is_better', label: '数值越大越优' },
              { value: 'smaller_is_better', label: '数值越小越优' },
            ]} onChange={(direction) => setCustomParameterDraft((current) => ({ ...current, direction }))} /></label>
            <label><span>DataHub 曲线</span><Select
              allowClear showSearch optionFilterProp="label" placeholder="可选；未绑定时显示为缺失参数"
              value={customParameterDraft.sourceColumnKey}
              options={props.availableSourceCurves}
              onChange={(sourceColumnKey) => {
                const source = props.availableSourceCurves.find((item) => item.value === sourceColumnKey);
                setCustomParameterDraft((current) => ({
                  ...current,
                  sourceColumnKey,
                  unit: current.unit || source?.unit || '',
                }));
              }}
            /></label>
          </div>
          {customParameterError && <Alert type="error" showIcon message={customParameterError} />}
          <div className="sweetspot-custom-parameter-actions"><Button type="primary" onClick={submitCustomParameter}>{editingCustomParameterId ? '保存修改' : '新增参数'}</Button></div>
        </div> : <Button className={`sweetspot-add-parameter-button is-${group}`} type="primary" onClick={() => startCustomParameter(group)}>
          + 新增{group === 'rq' ? '储层品质' : '工程品质'}参数
        </Button>}
      </section>)}
    </div>
    <div className="sweetspot-method-formula">
      <strong>甜点指数 SSI</strong><span>0.5 × RQ + 0.5 × CQ</span><Tag color="success">Ⅰ～Ⅴ级评价</Tag>
    </div>
  </>;

  const renderParameters = () => <>
    <div className="sweetspot-step-heading"><h3><span>3</span> 权重与参数配置</h3><Tag color={props.configValid ? 'success' : 'warning'}>{props.configValid ? '配置有效' : '需检查'}</Tag></div>
    <div className="sweetspot-parameter-toolbar">
      <label><span>储层类型</span><Select<ReservoirType> value={props.reservoirType} options={props.reservoirOptions} onChange={props.onReservoirTypeChange} /></label>
      <div><span>启用指标</span><strong>{props.enabledParameterCount} 项</strong></div>
      <div><span>当前方案</span><strong>{WEIGHT_SCHEME_LABELS[props.config.weightStrategy]}</strong></div>
    </div>
    <WeightSettingsPanel
      config={props.config}
      recommendedParameters={props.recommendedParameters}
      onChange={props.onConfigChange}
    />
  </>;

  const renderExecute = () => <>
    <div className="sweetspot-step-heading"><h3><span>4</span> 执行评价</h3><Tag color={computeReason ? 'default' : 'success'}>{computeReason ? '尚未就绪' : '配置已就绪'}</Tag></div>
    <dl className="sweetspot-compact-metrics sweetspot-execution-summary">
      <div><dt>DataHub 数据</dt><dd>{DATA_STATE_META[props.dataState].label}</dd></div>
      <div><dt>评价方法</dt><dd>甜点指数（SSI）</dd></div>
      <div><dt>权重方案</dt><dd>{WEIGHT_SCHEME_LABELS[props.config.weightStrategy]}</dd></div>
      <div><dt>可用评价参数</dt><dd>{props.quality.availableIndicatorIds.length} 项可用 / {props.enabledParameterCount} 项已启用</dd></div>
      <div><dt>深度采样点</dt><dd>{props.quality.pointCount} 个</dd></div>
    </dl>
    {computeReason && <Alert type="warning" showIcon message={computeReason} />}
    <Tooltip title={computeReason}><span className="sweetspot-primary-action-wrap" tabIndex={computeReason ? 0 : undefined}>
      <Button block type="primary" size="large" onClick={props.onCompute} disabled={Boolean(computeReason)} loading={props.busy}>{props.computeLabel}</Button>
    </span></Tooltip>
  </>;

  const renderReview = () => <section className="sweetspot-module-card sweetspot-review-controls">
      <div className="sweetspot-step-heading"><h3><span>5</span> 结果审核</h3><Tag color={props.config.reviewState === 'confirmed' ? 'success' : 'processing'}>{REVIEW_STATE_LABELS[props.config.reviewState ?? 'pending']}</Tag></div>
      <div className="sweetspot-review-tools">
        <div className="sweetspot-review-view-switch" role="group" aria-label="结果主视图">
          <Button className={`is-curves${props.reviewView === 'curves' ? ' is-active' : ''}`} type={props.reviewView === 'curves' ? 'primary' : 'default'} onClick={() => props.onReviewViewChange('curves')}>评价曲线</Button>
          <Button className={`is-engineering${props.reviewView === 'engineering' ? ' is-active' : ''}`} type={props.reviewView === 'engineering' ? 'primary' : 'default'} onClick={() => props.onReviewViewChange('engineering')}>工程分段方案</Button>
          <Button className={`is-data${props.reviewView === 'data' ? ' is-active' : ''}`} type={props.reviewView === 'data' ? 'primary' : 'default'} onClick={() => props.onReviewViewChange('data')}>评价数据明细</Button>
        </div>
        {props.reviewView === 'curves' && <div className="sweetspot-display-controls">
          <label><Switch size="small" checked={props.showRecommendations} onChange={props.onToggleRecommendations} /> 推荐井段</label>
          <label><Switch size="small" checked={props.showGradeBackground} onChange={props.onToggleGradeBackground} /> 等级背景</label>
          <label><Switch size="small" checked={props.showAnomalies} onChange={props.onToggleAnomalies} /> 异常点</label>
        </div>}
      </div>
      <Button className="sweetspot-review-confirm" type="primary" disabled={props.config.reviewState === 'confirmed'} onClick={() => props.onConfigChange({ ...props.config, reviewState: 'confirmed' })}>{props.config.reviewState === 'confirmed' ? '结果已确认' : '确认评价结果'}</Button>
  </section>;

  const renderHistory = () => props.history.length === 0
    ? <Alert type="info" message="暂无已保存版本" />
    : props.history.slice().sort((left, right) => {
      const leftActive = left.runId === props.activeRunId ? 1 : 0;
      const rightActive = right.runId === props.activeRunId ? 1 : 0;
      if (leftActive !== rightActive) return rightActive - leftActive;
      return right.updatedAt.localeCompare(left.updatedAt);
    }).map((run) => {
      const runDefinition = props.definitions.find((item) => item.methodId === run.methodId && item.version === run.methodVersion);
      return <div key={run.runId} className={`sweetspot-history-card${run.runId === props.activeRunId ? ' is-active' : ''}`}>
        <div className="sweetspot-history-head">
          <strong>{runDefinition?.documentation.displayName ?? run.methodName}</strong>
          <Tag color={run.runId === props.activeRunId ? 'green' : run.status === 'syncFailed' ? 'red' : 'default'}>
            {run.runId === props.activeRunId ? '当前版本' : RUN_STATUS_LABELS[run.status]}
          </Tag>
          <span className="sweetspot-history-time">{new Date(run.createdAt).toLocaleString()}</span>
        </div>
        <div className="sweetspot-history-summary">{run.points.length} 个评价点 · {WEIGHT_SCHEME_LABELS[run.config.weightStrategy]}</div>
        {run.syncError && <Alert type="error" showIcon message={run.syncError} />}
        <div className="sweetspot-history-actions">
          <Button onClick={() => props.onSelectRun(run)}>查看结果</Button>
          <Tooltip title={run.runId === props.activeRunId ? '该版本已是当前版本。' : !run.quality.canActivate ? '该版本未满足激活条件。' : undefined}><span tabIndex={!run.quality.canActivate || run.runId === props.activeRunId ? 0 : undefined}>
            <Button type="primary" disabled={!run.quality.canActivate || run.runId === props.activeRunId} onClick={() => props.onActivate(run)}>
              {run.status === 'syncFailed' ? '重试应用' : '应用'}
            </Button>
          </span></Tooltip>
        </div>
      </div>;
    });

  const renderSave = () => <>
    <div className="sweetspot-step-heading"><h3><span>6</span> 保存版本</h3>{props.draftRun && <Tag>{RUN_STATUS_LABELS[props.draftRun.status]}</Tag>}</div>
    <div className="sweetspot-save-layout">
      <section className="sweetspot-save-current">
        <h4>当前评价</h4>
        {props.draftRun ? <dl className="sweetspot-compact-metrics">
          <div><dt>评价区间</dt><dd>{props.intervalLabel}</dd></div>
          <div><dt>计算时间</dt><dd>{new Date(props.draftRun.createdAt).toLocaleString()}</dd></div>
          <div><dt>权重方案</dt><dd>{WEIGHT_SCHEME_LABELS[props.draftRun.config.weightStrategy]}</dd></div>
          <div><dt>评价点数</dt><dd>{props.draftRun.points.length} 个深度点</dd></div>
        </dl> : <Alert type="info" message="请先执行评价" />}
        <Tooltip title={saveReason}><span className="sweetspot-primary-action-wrap" tabIndex={saveReason ? 0 : undefined}>
          <Button block type="primary" size="large" onClick={props.onSave} disabled={Boolean(saveReason)} loading={props.busy}>保存评价版本</Button>
        </span></Tooltip>
      </section>
      <section className="sweetspot-history-panel">
        <div className="sweetspot-section-header"><h4>历史版本</h4><Button onClick={props.onRefreshHistory}>刷新</Button></div>
        <div className="sweetspot-history-list">{renderHistory()}</div>
      </section>
    </div>
  </>;

  const renderApply = () => {
    const isCurrent = Boolean(props.draftRun && props.draftRun.runId === props.activeRunId);
    return <>
      <div className="sweetspot-step-heading"><h3><span>7</span> 应用至压裂设计</h3><Tag color={isCurrent ? 'success' : 'default'}>{isCurrent ? '当前版本' : '待应用'}</Tag></div>
      <div className="sweetspot-apply-summary">
        <strong>{isCurrent ? '评价结果已成为当前版本' : '确认评价版本后应用到压裂设计'}</strong>
        <span>{props.draftRun ? `${props.intervalLabel} · ${WEIGHT_SCHEME_LABELS[props.draftRun.config.weightStrategy]}` : '尚未选择已保存版本'}</span>
      </div>
      <Tooltip title={activateReason}><span className="sweetspot-primary-action-wrap" tabIndex={activateReason ? 0 : undefined}><Button block type="primary" size="large" disabled={Boolean(activateReason) || isCurrent} loading={props.busy} onClick={() => props.draftRun && props.onActivate(props.draftRun)}>{isCurrent ? '已应用至压裂设计' : '应用至压裂设计'}</Button></span></Tooltip>
      <Tooltip title={props.activeRunId ? undefined : '请先应用一个有效评价版本'}><span className="sweetspot-primary-action-wrap" tabIndex={props.activeRunId ? undefined : 0}><Button block disabled={!props.activeRunId} onClick={props.onProceedToDesign}>进入压裂设计</Button></span></Tooltip>
    </>;
  };

  const contentByStep: Record<SweetSpotWorkflowStepKey, React.ReactNode> = {
    'data-check': renderDataCheck(), method: renderMethod(), parameters: renderParameters(),
    execute: renderExecute(), review: renderReview(), save: renderSave(), apply: renderApply(),
  };

  return <>
    <nav className="sweetspot-workflow-nav" aria-label="甜点评价工作流">
      {props.workflowSteps.map((step) => <Tooltip key={step.key} title={step.reason}>
        <span className="sweetspot-workflow-step-wrap">
          <button
            type="button"
            className={`sweetspot-workflow-step status-${step.status}${props.activeStep === step.key ? ' is-current' : ''}`}
            disabled={step.disabled}
            aria-current={props.activeStep === step.key ? 'step' : undefined}
            aria-pressed={props.activeStep === step.key}
            onClick={() => props.onStepChange(step.key)}
          >
            <span className="sweetspot-workflow-number">{step.order}</span>
            <span className="sweetspot-workflow-copy">
              <span className="sweetspot-workflow-label">{step.label}</span>
              <span className="sweetspot-workflow-status">{props.activeStep === step.key ? `当前第 ${step.order} 步` : SWEETSPOT_WORKFLOW_STATUS_LABELS[step.status]}</span>
            </span>
          </button>
        </span>
      </Tooltip>)}
    </nav>

    <main className={`sweetspot-workflow-panel${props.activeStep === 'review' ? ' is-review' : ''}`} aria-label="当前步骤工作区">
      {props.activeStep === 'review'
        ? contentByStep.review
        : <section className={`sweetspot-module-card is-${props.activeStep}`}>{contentByStep[props.activeStep]}</section>}
    </main>
  </>;
}


function WeightSettingsPanel({
  config, recommendedParameters, onChange,
}: {
  config: EvaluationMethodConfig;
  recommendedParameters: SweetSpotParameterConfig;
  onChange: (config: EvaluationMethodConfig) => void;
}) {
  const [matrices, setMatrices] = useState<Partial<Record<ParameterGroup, number[][]>>>({});
  useEffect(() => {
    setMatrices({ rq: config.ahp?.rq?.matrix, cq: config.ahp?.cq?.matrix });
  }, [config.ahp?.cq?.matrix, config.ahp?.rq?.matrix]);
  const updateWeight = (group: ParameterGroup, id: string, weight: number) => onChange({
    ...config,
    parameters: {
      ...config.parameters,
      [group]: config.parameters[group].map((item) => item.id === id ? { ...item, weight } : item),
    },
  });
  const updateEnabled = (group: ParameterGroup, id: string, enabled: boolean) => onChange({
    ...config,
    ahp: undefined,
    parameters: {
      ...config.parameters,
      [group]: config.parameters[group].map((item) => item.id === id ? { ...item, enabled } : item),
    },
  });
  const initializeMatrix = (group: ParameterGroup) => {
    const size = config.parameters[group].filter((item) => item.enabled).length;
    setMatrices((current) => ({
      ...current,
      [group]: Array.from({ length: size }, (_, row) => Array.from({ length: size }, (_, column) => row === column ? 1 : 1)),
    }));
  };
  const updateMatrix = (group: ParameterGroup, row: number, column: number, value: number) => {
    setMatrices((current) => {
      const matrix = current[group]?.map((item) => [...item]);
      if (!matrix || row === column || value <= 0) return current;
      matrix[row][column] = value;
      matrix[column][row] = 1 / value;
      return { ...current, [group]: matrix };
    });
  };
  const calculateMatrix = (group: ParameterGroup) => {
    const matrix = matrices[group];
    if (!matrix) return;
    const result = calculateAHP(matrix);
    onChange({ ...config, ahp: { ...config.ahp, [group]: result } });
  };

  const changeWeightScheme = (next: WeightStrategy) => onChange(
    switchWeightScheme(config, next, recommendedParameters),
  );
  const weightHelpTerm = config.weightStrategy === 'fixed'
    ? 'recommendedWeight'
    : config.weightStrategy === 'user' ? 'customWeight' : 'ahp';
  return <div className="sweetspot-settings-panel">
    <section className="sweetspot-settings-section" aria-labelledby="sweetspot-weight-heading">
      <strong id="sweetspot-weight-heading" className="sweetspot-inline-help">权重方案 <SweetSpotHelp term={weightHelpTerm} /></strong>
      <Space wrap style={{ display: 'flex', marginTop: 8 }}>
        <Segmented
          className={`sweetspot-weight-scheme-selector is-${config.weightStrategy}`}
          value={config.weightStrategy}
          options={[{ value: 'fixed', label: '预设权重' }, { value: 'user', label: '自定义权重' }, { value: 'ahp', label: '层次分析（AHP）' }]}
          onChange={(weightStrategy) => changeWeightScheme(weightStrategy as WeightStrategy)}
        />
      </Space>
    </section>

    <section className="sweetspot-settings-section" aria-labelledby="sweetspot-threshold-heading">
      <strong id="sweetspot-threshold-heading" className="sweetspot-inline-help">等级阈值 <SweetSpotHelp term="gradeThreshold" /></strong>
      <Space style={{ display: 'flex', marginTop: 8 }} wrap>
        {(['classI', 'classII', 'classIII', 'classIV'] as const).map((key, index) => <label key={key} style={{ fontSize: 12 }}>
          {['Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ'][index]}级下限&nbsp;
          <InputNumber min={0} max={1} step={0.05} value={config.gradeThresholds[key]} onChange={(value) => onChange({
            ...config, gradeThresholds: { ...config.gradeThresholds, [key]: typeof value === 'number' ? value : 0 },
          })} />
        </label>)}
      </Space>
    </section>

    <section className="sweetspot-settings-section sweetspot-settings-section--wide" aria-labelledby="sweetspot-indicator-heading">
      <strong id="sweetspot-indicator-heading" className="sweetspot-inline-help">评价指标与指标权重</strong>
      <div className="sweetspot-indicator-groups">
        {(['rq', 'cq'] as ParameterGroup[]).map((group) => <div key={group} className="sweetspot-indicator-group">
          <div className="sweetspot-indicator-group-head">
            <Tag color={group === 'rq' ? 'blue' : 'orange'}>{group === 'rq' ? '储层品质 RQ' : '工程品质 CQ'}</Tag>
            <span>{config.parameters[group].filter((item) => item.enabled).length}/{config.parameters[group].length} 已启用</span>
          </div>
          {config.parameters[group].map((item) => <div key={item.id} className="sweetspot-indicator-row">
            <label>
              <Switch size="small" checked={item.enabled} onChange={(enabled) => updateEnabled(item.group, item.id, enabled)} />
              <span><strong>{item.label}{OPTIONAL_PARAMETER_IDS.has(item.id) ? '（可选）' : ''}</strong><small>{item.unit}</small></span>
            </label>
            <div className="sweetspot-weight-field">
              <span>权重</span>
              <InputNumber
                min={0}
                step={0.05}
                disabled={config.weightStrategy !== 'user'}
                value={config.weightStrategy === 'ahp'
                  ? config.ahp?.[item.group]?.weights[config.parameters[item.group].filter((parameter) => parameter.enabled).findIndex((parameter) => parameter.id === item.id)] ?? item.weight
                  : item.weight}
                onChange={(value) => updateWeight(item.group, item.id, typeof value === 'number' ? value : 0)}
              />
            </div>
          </div>)}
        </div>)}
      </div>
    </section>

    {config.weightStrategy === 'ahp' && <section className="sweetspot-settings-section sweetspot-settings-section--wide">
      <strong className="sweetspot-inline-help">层次分析（AHP）判断矩阵 <SweetSpotHelp term="ahp" /></strong>
      <p style={{ fontSize: 12, color: '#4F6680' }}>判断矩阵由用户录入成对比较值；编辑上三角时自动维护互反值。</p>
      {(['rq', 'cq'] as ParameterGroup[]).map((group) => {
        const enabled = config.parameters[group].filter((item) => item.enabled);
        const matrix = matrices[group];
        const result = config.ahp?.[group];
        return <div key={group} style={{ marginBottom: 18, padding: 10, border: '1px solid #D6DAE0', borderRadius: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><strong>{group.toUpperCase()}（{enabled.length}指标）</strong>
            <Button size="small" onClick={() => initializeMatrix(group)}>创建空白单位矩阵</Button>
            {matrix && <Button size="small" type="primary" onClick={() => calculateMatrix(group)}>计算并保存检验结果</Button>}
          </div>
          {matrix && <div className="sweetspot-matrix-scroll" style={{ marginTop: 8 }}><table><tbody>{matrix.map((row, rowIndex) => <tr key={rowIndex}>{row.map((value, columnIndex) => <td key={columnIndex} style={{ padding: 2 }}>
            <InputNumber size="small" min={0.001} disabled={rowIndex >= columnIndex} value={Number(value.toFixed(4))} onChange={(next) => updateMatrix(group, rowIndex, columnIndex, typeof next === 'number' ? next : value)} style={{ width: 82 }} />
          </td>)}</tr>)}</tbody></table></div>}
          {result && <Alert style={{ marginTop: 8 }} type={result.consistent ? 'success' : 'error'} showIcon message={
            `λmax=${result.lambdaMax.toFixed(4)}，CI=${result.ci.toFixed(4)}，CR=${Number.isFinite(result.cr) ? result.cr.toFixed(4) : '∞'}；${result.consistent ? '通过一致性检验' : 'CR≥0.1或矩阵非互反，禁止确认使用'} `
          } description={result.warnings.join('；') || undefined} />}
        </div>;
      })}
    </section>}
  </div>;
}
